import { type Store } from '@datorama/akita';
import { type AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { type ErrorObject } from 'types/models';

/**
 * Gateway / upstream-unavailable statuses. These are transient: the request can
 * simply be retried. They must NOT trigger a full-page `window.location.reload()`
 * — when the failure is persistent (e.g. a backend that keeps timing out) that
 * turns into an endless blank reload loop. Instead we surface a recoverable error
 * so the UI can show a retry affordance.
 */
const GATEWAY_ERROR_STATUSES = [502, 503, 504];

export function handleError(error: ErrorObject | AxiosError, store: Store): void {
   const status = error?.response?.code ?? error?.response?.status;

   if (status === 401) {
      return window.location.assign(`${window.location.origin}/login`);
   }

   if (error?.code !== 'ERR_CANCELED') {
      const isGatewayError = GATEWAY_ERROR_STATUSES.includes(status);

      store.setError({
         code: status ?? error?.response?.data?.code,
         variant: 'error',
         recoverable: isGatewayError,
         message: isGatewayError
            ? 'The server took too long to respond or is temporarily unavailable. Please try again in a moment.'
            : `${error?.response?.data?.message ?? error.message ?? ''} ${error?.response?.data?.error ?? ''}`,
         key: `${store?.storeName}#${uuidv4()}`,
         store: store?.storeName,
      });
   }
   return store.setLoading(false);
}
